/**
 * Thanksgiving icons — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingIconsPack: ToilePack = {
    slug: 'thanksgiving-icons',
    category: 'season',
    name: 'Thanksgiving icons',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'turkey', label: 'Turkey icon', subject: 'A turkey with a fanned tail reduced to one simple glyph, built to read small, isolated'},
        {key: 'pie', label: 'Pie icon', subject: 'A pie with a crimped crust as one simple glyph, built to read small, isolated'},
        {key: 'leaf', label: 'Leaf icon', subject: 'A maple leaf as one simple glyph, built to read small, isolated'},
    ],
    styles: [
        {key: 'harvest', label: 'Harvest flat', style: 'flat harvest illustration, simplified shapes, no outlines, matte colour', palette: 'Mustard, rust, pumpkin orange, olive and cream', framing: 'Centered on a solid cream background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage americana', style: 'vintage americana illustration, halftone grain, slight misregistration, warm', palette: 'Faded rust, brown, mustard and cream', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolour illustration, loose edges, paper texture showing', palette: 'Warm autumn washes of orange, ochre and brown', framing: 'Centered with white paper around it'},
    ],
    directions: ['a cornucopia icon instead', 'as a rounded app icon', 'in a single flat color'],
    directionHint: 'try: a cornucopia icon instead',
    seo: {
        title: 'Thanksgiving Icons: Turkey, Pie & Leaf Glyphs',
        metaDescription: 'Nine Thanksgiving icons — turkey, pie, leaf — built to read at 32 pixels, in harvest flat, vintage americana, and watercolor styles.',
        h1: 'Thanksgiving icon set: turkey, pie, leaf glyphs',
        tagline: 'Nine icons, three glyphs, three treatments each.',
        intro: 'Nine Thanksgiving glyph icons — a turkey, a pie, a maple leaf — each reduced to one simple shape and built to stay legible at small icon size, in a flat harvest, a vintage americana, and a watercolor treatment. Set against a plain background with no lettering, ready to drop into a menu, app, or slide deck.',
        useCases: [
            'a menu or recipe card section marker',
            'an app or website seasonal icon set',
            'a slide deck or infographic bullet icon',
            'a printable label or tag accent',
        ],
        faq: [
            {q: 'Do these icons stay legible at small sizes?', a: 'Yes — each glyph is simplified down to one clear shape specifically so it holds up at roughly 32 pixels, not just at full size.'},
            {q: 'Are the icons a matched set or three unrelated styles?', a: 'Each of the three subjects — turkey, pie, leaf — comes in the same three styles, so you can pick one register and use all three glyphs together as a set.'},
            {q: "What's the difference between the three styles?", a: 'Harvest flat uses simplified matte shapes, vintage americana adds halftone grain and warm faded color, and watercolor uses soft loose edges with visible paper texture.'},
            {q: 'Can I recolor the icons to match my brand?', a: 'The flat and vintage styles are simple enough to recolor in most image editors; the watercolor style is more textured and recolors less cleanly.'},
        ],
    },
}
